import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import { InputTextarea } from 'primereact/inputtextarea'
import { Message } from 'primereact/message'
import { Divider } from 'primereact/divider'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { Gender, MembershipStatus, PaymentStatus, Member } from 'shared'
import axios from 'axios'

const updateMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  dateOfBirth: z.date().optional(),
  gender: z.nativeEnum(Gender).optional(),
  membershipStatus: z.nativeEnum(MembershipStatus),
  joinDate: z.date(),
  membershipFee: z.number().min(0).optional(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  earnsIncome: z.boolean(),
  notes: z.string().optional(),
  address: z.object({
    street1: z.string().optional(),
    street2: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().optional(),
    country: z.string().optional(),
  }).optional(),
})

type UpdateMemberFormData = z.infer<typeof updateMemberSchema>

interface EditMemberModalProps {
  visible: boolean
  member: Member | null
  onHide: () => void
  onSuccess: () => void
  onDelete: () => void
}

const EditMemberModal: React.FC<EditMemberModalProps> = ({ visible, member, onHide, onSuccess, onDelete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string>('')
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [formData, setFormData] = useState<UpdateMemberFormData | null>(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)


  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateMemberFormData>({
    resolver: zodResolver(updateMemberSchema),
  })

  // Reset form when member changes or modal opens
  useEffect(() => {
    if (member && visible) {
      console.log('Member membershipFee value:', member.membershipFee, 'type:', typeof member.membershipFee)
      reset({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        phone: member.phone || '',
        dateOfBirth: member.dateOfBirth ? new Date(member.dateOfBirth) : undefined,
        gender: member.gender || undefined,
        membershipStatus: member.membershipStatus || MembershipStatus.PENDING,
        joinDate: member.joinDate ? new Date(member.joinDate) : new Date(),
        membershipFee: typeof member.membershipFee === 'number' ? member.membershipFee : (member.membershipFee ? parseFloat(String(member.membershipFee)) : 12),
        paymentStatus: member.paymentStatus || PaymentStatus.PENDING,
        earnsIncome: member.earnsIncome || false,
        notes: member.notes || '',
        address: member.address ? {
          street1: member.address.street1 || '',
          street2: member.address.street2 || '',
          city: member.address.city || '',
          zipCode: member.address.zipCode || '',
          country: member.address.country || 'Germany',
        } : {
          street1: '',
          street2: '',
          city: '',
          zipCode: '',
          country: 'Germany',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id, visible])

  const genderOptions = [
    { label: 'Male', value: Gender.MALE },
    { label: 'Female', value: Gender.FEMALE },
    { label: 'Other', value: Gender.OTHER },
    { label: 'Prefer not to say', value: Gender.PREFER_NOT_TO_SAY },
  ]

  const statusOptions = [
    { label: 'Active', value: MembershipStatus.ACTIVE },
    { label: 'Pending', value: MembershipStatus.PENDING },
    { label: 'Inactive', value: MembershipStatus.INACTIVE },
    { label: 'Suspended', value: MembershipStatus.SUSPENDED },
    { label: 'Terminated', value: MembershipStatus.TERMINATED },
  ]

  const paymentStatusOptions = [
    { label: 'Paid', value: PaymentStatus.PAID },
    { label: 'Pending', value: PaymentStatus.PENDING },
    { label: 'Overdue', value: PaymentStatus.OVERDUE },
    { label: 'Exempt', value: PaymentStatus.EXEMPT },
  ]


  const onSubmit = (data: UpdateMemberFormData) => {
    console.log('Form submitted with data:', data)
    console.log('Form errors:', errors)
    setFormData(data)
    setShowConfirmation(true)
  }

  const handleConfirmedSubmit = async () => {
    if (!formData || !member?.id) return

    try {
      setIsSubmitting(true)
      setError('')

      // Add default member role since we removed the dropdown
      // Also ensure membershipFee has a valid default if undefined
      const membershipFee = formData.membershipFee ?? 12
      console.log('MembershipFee before sending:', membershipFee, typeof membershipFee)

      const dataWithRole = {
        ...formData,
        memberRoles: ['MEMBER'],
        membershipFee: membershipFee
      }

      console.log('Data being sent to API:', dataWithRole)

      const response = await axios.put(`${API_BASE_URL}/api/members/${member.id}`, dataWithRole, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.data.success) {
        setShowConfirmation(false)
        onSuccess()
        onHide()
      } else {
        setError(response.data.error?.message || 'Failed to update member')
      }
    } catch (err: unknown) {
      console.error('Failed to update member:', err)
      const errorMessage = err instanceof Error && 'response' in err
        ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        : 'Failed to update member'
      setError(errorMessage || 'Failed to update member')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDialogHide = () => {
    if (!isSubmitting && !isDeleting) {
      setError('')
      setShowConfirmation(false)
      setShowDeleteConfirmation(false)
      setDeleteConfirmationText('')
      setFormData(null)
      onHide()
    }
  }

  const handleDeleteMember = async () => {
    if (!member?.id || deleteConfirmationText !== 'i am sure') return

    try {
      setIsDeleting(true)
      setError('')

      const response = await axios.delete(`${API_BASE_URL}/api/members/${member.id}`)

      if (response.data.success) {
        console.log('✅ Member deleted successfully')
        setShowDeleteConfirmation(false)
        setDeleteConfirmationText('')
        onDelete()
        onHide()
      } else {
        setError(response.data.error?.message || 'Failed to delete member')
      }
    } catch (err: unknown) {
      console.error('❌ Failed to delete member:', err)
      const errorMessage = err instanceof Error && 'response' in err
        ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        : 'Failed to delete member'
      setError(errorMessage || 'Failed to delete member')
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteConfirmation = () => {
    setDeleteConfirmationText('')
    setShowDeleteConfirmation(true)
  }

  const cancelDelete = () => {
    setDeleteConfirmationText('')
    setShowDeleteConfirmation(false)
  }

  const renderConfirmationContent = () => {
    if (!formData || !member) return null

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Please confirm the updated member details:</h3>

        <div className="grid grid-cols-2 gap-4">
          <div><strong>Member #:</strong> {member.memberNumber?.toString().padStart(3, '0') || '000'}</div>
          <div><strong>Name:</strong> {formData.firstName} {formData.lastName}</div>
          <div><strong>Email:</strong> {formData.email}</div>
          <div><strong>Phone:</strong> {formData.phone || 'N/A'}</div>
          <div><strong>Status:</strong> {formData.membershipStatus}</div>
          <div><strong>Join Date:</strong> {formData.joinDate.toLocaleDateString()}</div>
          <div><strong>Payment Status:</strong> {formData.paymentStatus}</div>
          <div><strong>Earns Income:</strong> {formData.earnsIncome ? 'Yes' : 'No'}</div>
        </div>

        {formData.address && (
          <div>
            <h4 className="font-semibold">Address:</h4>
            <p>{formData.address.street1}</p>
            {formData.address.street2 && <p>{formData.address.street2}</p>}
            <p>{formData.address.zipCode} {formData.address.city}</p>
            <p>{formData.address.country}</p>
          </div>
        )}

        {formData.notes && (
          <div>
            <h4 className="font-semibold">Notes:</h4>
            <p>{formData.notes}</p>
          </div>
        )}
      </div>
    )
  }

  if (!member) return null

  return (
    <>
      <Dialog
        visible={visible}
        onHide={handleDialogHide}
        header={`Edit Member - ${member.firstName} ${member.lastName}`}
        modal
        className="w-11/12 max-w-4xl"
        draggable={false}
        resizable={false}
      >
        <form onSubmit={handleSubmit(onSubmit, (errors) => {
          console.log('Form validation errors:', errors)
          console.log('MembershipFee error details:', errors.membershipFee)
        })} className="space-y-6">
          {error && (
            <Message severity="error" text={error} className="w-full" />
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="firstName"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.firstName ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.firstName && (
                  <small className="text-red-500 mt-1 block">{errors.firstName.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="lastName"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.lastName ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.lastName && (
                  <small className="text-red-500 mt-1 block">{errors.lastName.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="email"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.email ? 'p-invalid' : ''}`}
                      type="email"
                    />
                  )}
                />
                {errors.email && (
                  <small className="text-red-500 mt-1 block">{errors.email.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="phone"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className="w-full"
                      type="tel"
                    />
                  )}
                />
              </div>

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="gender"
                      value={field.value}
                      onChange={field.onChange}
                      options={genderOptions}
                      placeholder="Select gender"
                      className="w-full"
                      showClear
                    />
                  )}
                />
              </div>

              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="dateOfBirth"
                      value={field.value}
                      onChange={field.onChange}
                      className="w-full"
                      dateFormat="dd.mm.yy"
                      showIcon
                      maxDate={new Date()}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <Divider />

          {/* Membership Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Membership Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="membershipStatus" className="block text-sm font-medium text-gray-700 mb-2">
                  Membership Status *
                </label>
                <Controller
                  name="membershipStatus"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="membershipStatus"
                      value={field.value}
                      onChange={field.onChange}
                      options={statusOptions}
                      className={`w-full ${errors.membershipStatus ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.membershipStatus && (
                  <small className="text-red-500 mt-1 block">{errors.membershipStatus.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="joinDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Join Date *
                </label>
                <Controller
                  name="joinDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="joinDate"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.joinDate ? 'p-invalid' : ''}`}
                      dateFormat="dd.mm.yy"
                      showIcon
                      maxDate={new Date()}
                    />
                  )}
                />
                {errors.joinDate && (
                  <small className="text-red-500 mt-1 block">{errors.joinDate.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Status *
                </label>
                <Controller
                  name="paymentStatus"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="paymentStatus"
                      value={field.value}
                      onChange={field.onChange}
                      options={paymentStatusOptions}
                      className={`w-full ${errors.paymentStatus ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.paymentStatus && (
                  <small className="text-red-500 mt-1 block">{errors.paymentStatus.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="membershipFee" className="block text-sm font-medium text-gray-700 mb-2">
                  Membership Fee (€)
                </label>
                <Controller
                  name="membershipFee"
                  control={control}
                  render={({ field: { onChange, value, ref, ...rest } }) => {
                    const [localValue, setLocalValue] = useState(value?.toString() ?? '')

                    useEffect(() => {
                      setLocalValue(value?.toString() ?? '')
                    }, [value])

                    return (
                      <InputText
                        id="membershipFee"
                        {...rest}
                        ref={ref}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onBlur={() => {
                          const num = parseFloat(localValue)
                          onChange(localValue === '' ? undefined : (isNaN(num) ? undefined : num))
                        }}
                        className={`w-full ${errors.membershipFee ? 'p-invalid' : ''}`}
                        keyfilter="num"
                      />
                    )
                  }}
                />
                {errors.membershipFee && (
                  <small className="text-red-500 mt-1 block">{errors.membershipFee.message}</small>
                )}
              </div>

              <div className="md:col-span-2">
                <Controller
                  name="earnsIncome"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <Checkbox
                        id="earnsIncome"
                        checked={field.value}
                        onChange={field.onChange}
                        className="mr-2"
                      />
                      <label htmlFor="earnsIncome" className="text-sm font-medium text-gray-700">
                        Member earns income
                      </label>
                    </div>
                  )}
                />
              </div>

            </div>
          </div>

          <Divider />

          {/* Address Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="address.street1" className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <Controller
                  name="address.street1"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.street1"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.street1 ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.street1 && (
                  <small className="text-red-500 mt-1 block">{errors.address.street1.message}</small>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="address.street2" className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address 2
                </label>
                <Controller
                  name="address.street2"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.street2"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className="w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label htmlFor="address.city" className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <Controller
                  name="address.city"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.city"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.city ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.city && (
                  <small className="text-red-500 mt-1 block">{errors.address.city.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="address.zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code
                </label>
                <Controller
                  name="address.zipCode"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.zipCode"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.zipCode ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.zipCode && (
                  <small className="text-red-500 mt-1 block">{errors.address.zipCode.message}</small>
                )}
              </div>

              <div>
                <label htmlFor="address.country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <Controller
                  name="address.country"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="address.country"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className={`w-full ${errors.address?.country ? 'p-invalid' : ''}`}
                    />
                  )}
                />
                {errors.address?.country && (
                  <small className="text-red-500 mt-1 block">{errors.address.country.message}</small>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Notes</h3>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <InputTextarea
                  id="notes"
                  value={field.value || ''}
                  onChange={field.onChange}
                  className="w-full"
                  rows={4}
                  placeholder="Any additional notes about the member..."
                />
              )}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={handleDialogHide}
              disabled={isSubmitting || isDeleting}
            />
            <Button
              type="button"
              label="Delete Member"
              icon="pi pi-trash"
              className="p-button-danger"
              onClick={openDeleteConfirmation}
              disabled={isSubmitting || isDeleting}
            />
            <Button
              type="submit"
              label="Review & Save"
              icon="pi pi-check"
              disabled={isSubmitting || isDeleting}
            />
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        visible={showConfirmation}
        onHide={() => !isSubmitting && setShowConfirmation(false)}
        message={renderConfirmationContent()}
        header="Confirm Member Update"
        icon="pi pi-question-circle"
        accept={handleConfirmedSubmit}
        acceptLabel="Update Member"
        acceptClassName="p-button-success"
        reject={() => setShowConfirmation(false)}
        rejectLabel="Go Back"
        rejectClassName="p-button-outlined"
      />

      <Dialog
        visible={showDeleteConfirmation}
        onHide={() => !isDeleting && cancelDelete()}
        header={`Delete Member: ${member?.firstName} ${member?.lastName}`}
        style={{ width: 'min(95vw, 450px)' }}
        modal
        closable={!isDeleting}
      >
        <div className="p-4">
          <div className="mb-4">
            <i className="pi pi-exclamation-triangle text-6xl text-red-500 block text-center mb-4"></i>
            <p className="text-center mb-4">
              <strong>This action cannot be undone!</strong>
            </p>
            <p className="text-center mb-4">
              You are about to permanently delete member <strong>{member?.firstName} {member?.lastName}</strong> (#{member?.memberNumber}).
              All associated data will be removed.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              To confirm deletion, type: <strong className="text-red-600">i am sure</strong>
            </label>
            <InputText
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder="Type 'i am sure' to confirm"
              className="w-full"
              disabled={isDeleting}
            />
          </div>

          {error && (
            <Message severity="error" text={error} className="mb-4" />
          )}

          <div className="flex justify-end space-x-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={cancelDelete}
              disabled={isDeleting}
            />
            <Button
              label={isDeleting ? 'Deleting...' : 'Delete Member'}
              icon={isDeleting ? 'pi pi-spinner pi-spin' : 'pi pi-trash'}
              className="p-button-danger"
              onClick={handleDeleteMember}
              disabled={isDeleting || deleteConfirmationText !== 'i am sure'}
            />
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default EditMemberModal